import { type FunctionComponent } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { AppConfigType, type AppVersionConfig } from "@fitness-app/data-models/entities/TrainerConfig";

import { supabase } from "~/store/initializeStore";

// Plain dotted-version comparison (e.g. "0.269.3"). Returns -1/0/1.
const compareDottedVersions = (a: string, b: string): number => {
  const parse = (value: string) => (value.match(/\d+/g) ?? []).map((part) => parseInt(part, 10) || 0);
  const parsedA = parse(a);
  const parsedB = parse(b);
  const length = Math.max(parsedA.length, parsedB.length);

  for (let i = 0; i < length; i++) {
    const numA = parsedA[i] ?? 0;
    const numB = parsedB[i] ?? 0;
    if (numA > numB) return 1;
    if (numA < numB) return -1;
  }

  return 0;
};

const AppVersionAlert: FunctionComponent = () => {
  const { t } = useTranslation("common");
  const currentVersion = import.meta.env.PACKAGE_VERSION;

  const { data } = useQuery({
    queryKey: ["app_config", AppConfigType.APP_VERSION],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_config")
        .select()
        .eq("type", AppConfigType.APP_VERSION)
        .single<AppVersionConfig>();

      if (error) {
        throw error;
      }

      return data;
    },
    // Refetch periodically so trainers learn about a fresh deploy without a hard reload.
    refetchInterval: 1000 * 60 * 10,
    refetchOnWindowFocus: true,
  });

  const admin = data?.data.admin;

  if (!admin?.current || !currentVersion) {
    return null;
  }

  const isCritical = admin.minRequired ? compareDottedVersions(currentVersion, admin.minRequired) < 0 : false;
  const isOutdated = isCritical || compareDottedVersions(currentVersion, admin.current) < 0;

  if (!isOutdated) {
    return null;
  }

  const accent = isCritical
    ? {
        card: "border-red-500/30 bg-red-500/10",
        icon: "text-red-400",
        button: "bg-red-600 hover:bg-red-500",
      }
    : {
        card: "border-amber-500/30 bg-amber-500/10",
        icon: "text-amber-400",
        button: "bg-amber-600 hover:bg-amber-500",
      };

  return (
    <div className="mx-3 mb-3">
      <div className={`rounded-xl border p-3 ${accent.card}`}>
        <div className="flex items-start gap-2.5">
          <TriangleAlert className={`mt-0.5 h-4 w-4 shrink-0 ${accent.icon}`} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-snug text-white">{t("newAdminVersion.title")}</p>
            <p className="mt-1 text-xs leading-relaxed text-gray-300">
              {t(isCritical ? "newAdminVersion.criticalDescription" : "newAdminVersion.description")}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className={`mt-3 flex w-full cursor-pointer appearance-none items-center justify-center gap-2 rounded-lg border-0 px-3 py-2 text-sm font-medium text-white transition-colors ${accent.button}`}
        >
          <RefreshCw className="h-4 w-4" />
          {t("newAdminVersion.refresh")}
        </button>
      </div>
    </div>
  );
};

export default AppVersionAlert;
